import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import _ from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { shallow } from "zustand/shallow";
import nutritionFooter from "../assets/nutrition-footer.png";
import withWidgetChildrenLoader from "@/hocs/WidgetContainer/withWidgetChildrenLoader";
import useMetadataStore from "@/state/metadata";
import TooltipButton from "../layouts/TooltipButton";

import "@inovua/reactdatagrid-community/index.css";
import "./WidgetDataQuality.css";

import { pull } from "../../utils";

const WidgetDataQuality = ({ setLoading }) => {
  const { t, i18n } = useTranslation();

  const { orgUnits, orgUnitGroups } = useMetadataStore(
    (state) => ({
      orgUnits: state.oupOrgUnits,
      orgUnitGroups: state.oupOrgUnitGroups,
    }),
    shallow
  );

  const [dataNutri, setDataNutri] = useState();

  const getStyleByRangeOfValue = ({ red, yellow, green }) => {
    if (red) {
      return "#FF5757";
    }
    if (yellow) {
      return "#FFDE59";
    }
    if (green) {
      return "#99E573";
    }
    return "#C0C0C0";
  };

  const valueInRange = (value, range) => {
    return value >= range.min && value <= range.max;
  };

  const listLegend = [
    // w8XQmI94Spv.REPORTING_RATE - Completeness
    {
      red: { min: 0, max: 89, background: "#FF5757" },
      yellow: { min: 90, max: 90, background: "#FFDE59" },
      green: { min: 91, max: Infinity, background: "#99E573" },
    },
    // w8XQmI94Spv.REPORTING_RATE_ON_TIME - Timeliness
    {
      red: { min: 0, max: 77, background: "#FF5757" },
      yellow: { min: 78, max: 91, background: "#FFDE59" },
      green: { min: 92, max: Infinity, background: "#99E573" },
    },
    // vTHzAXlQiF6 - Correctness
    {
      red: { min: 0, max: 81, background: "#FF5757" },
      yellow: { min: 82, max: 91, background: "#FFDE59" },
      green: { min: 92, max: Infinity, background: "#99E573" },
    },
    // uEYUCTNMfdk - Consistency
    {
      red: { min: 0, max: 82, background: "#FF5757" },
      yellow: { min: 83, max: 93, background: "#FFDE59" },
      green: { min: 94, max: Infinity, background: "#99E573" },
    },
    // QOYBJGW55uA - Overall data quality
    {
      red: { min: 0, max: 80, background: "#FF5757" },
      yellow: { min: 81, max: 90, background: "#FFDE59" },
      green: { min: 91, max: Infinity, background: "#99E573" },
    },
  ];

  const listData = useMemo(
    () => [
      {
        value: "1",
        getValue: (ouId) => {
          const value = dataNutri[ouId]?.find((rows) => {
            return rows[0] === "w8XQmI94Spv.REPORTING_RATE";
          })?.[2];

          return value
            ? {
                value: value,
                style: getStyleByRangeOfValue({
                  red: valueInRange(value, listLegend[0].red),
                  yellow: valueInRange(value, listLegend[0].yellow),
                  green: valueInRange(value, listLegend[0].green),
                }),
              }
            : null;
        },
      },
      {
        value: "2",
        getValue: (ouId) => {
          const value = dataNutri[ouId]?.find((rows) => {
            return rows[0] === "w8XQmI94Spv.REPORTING_RATE_ON_TIME";
          })?.[2];

          return value
            ? {
                value: value,
                style: getStyleByRangeOfValue({
                  red: valueInRange(value, listLegend[1].red),
                  yellow: valueInRange(value, listLegend[1].yellow),
                  green: valueInRange(value, listLegend[1].green),
                }),
              }
            : null;
        },
      },
      {
        value: "3",
        getValue: (ouId) => {
          const value = dataNutri[ouId]?.find((rows) => {
            return rows[0] === "vTHzAXlQiF6";
          })?.[2];

          return value
            ? {
                value: value,
                style: getStyleByRangeOfValue({
                  red: valueInRange(value, listLegend[2].red),
                  yellow: valueInRange(value, listLegend[2].yellow),
                  green: valueInRange(value, listLegend[2].green),
                }),
              }
            : null;
        },
      },
      {
        value: "4",
        getValue: (ouId) => {
          const value = dataNutri[ouId]?.find((rows) => {
            return rows[0] === "uEYUCTNMfdk";
          })?.[2];

          return value
            ? {
                value: value,
                style: getStyleByRangeOfValue({
                  red: valueInRange(value, listLegend[3].red),
                  yellow: valueInRange(value, listLegend[3].yellow),
                  green: valueInRange(value, listLegend[3].green),
                }),
              }
            : null;
        },
      },
      {
        value: "5",
        getValue: (ouId) => {
          const value = dataNutri[ouId]?.find((rows) => {
            return rows[0] === "QOYBJGW55uA";
          })?.[2];

          return value
            ? {
                de: "QOYBJGW55uA",
                value: value,
                style: getStyleByRangeOfValue({
                  red: valueInRange(value, listLegend[4].red),
                  yellow: valueInRange(value, listLegend[4].yellow),
                  green: valueInRange(value, listLegend[4].green),
                }),
              }
            : null;
        },
      },
    ],
    [dataNutri, orgUnitGroups, orgUnits]
  );

  const listCapitalOrDistrict = useMemo(() => {
    return (
      dataNutri &&
      Object.keys(dataNutri)
        .map((ou) => orgUnits.find((item) => item.id === ou))
        .filter((ou) => ou.code)
      // .sort(function (a, b) {
      //   // sort by listCapitalOrDistrictByOrder
      //   const aIndex = listCapitalOrDistrictByOrder.findIndex(
      //     (item) => item.code === a.code
      //   );
      //   const bIndex = listCapitalOrDistrictByOrder.findIndex(
      //     (item) => item.code === b.code
      //   );

      //   return aIndex - bIndex;

      //   // if (a.code < b.code) {
      //   //   return -1;
      //   // }
      //   // if (a.code > b.code) {
      //   //   return 1;
      //   // }
      //   // return 0;
      // })
    );
  }, [dataNutri]);

  useEffect(() => {
    (async () => {
      setLoading(true);

      // TODO: Real data
      // const result = await pull("/api/getDashboard9WidgetDataQuality");
      // const completenessDataGroupByProvince = _.groupBy(result.data.rows, 1);
      // console.log({ completenessDataGroupByProvince });

      // Mock data
      const metadatas = [
        "w8XQmI94Spv.REPORTING_RATE",
        "w8XQmI94Spv.REPORTING_RATE_ON_TIME",
        "vTHzAXlQiF6",
        "uEYUCTNMfdk",
        "QOYBJGW55uA",
      ];

      const mockData = {
        W6sNfkJcXGC: [90, 74, 82, 100, 86], //01 Vientiane Capital
        YvLOmtTQD6b: [96, 81, 84, 98, 90], //02 Phongsali
        XKGgynPS1WZ: [97, 80, 82, 96, 89], //03 Louangnamtha
        rO2RVJWHpCe: [98, 77, 79, 100, 88], //04 Oudomxai
        FRmrFTE63D0: [98, 90, 92, 100, 95], //05 Bokeo
        MBZYTqkEgwf: [100, 77, 77, 100, 88], //06 Louangphabang
        hdeC7uX9Cko: [100, 92, 92, 100, 96], //07 Houaphan
        RdNV4tTRNEo: [99, 70, 71, 100, 85], //08 Xainyabouli
        VWGSudnonm5: [99, 85, 85, 100, 92], //09 Xiangkhouang
        quFXhkOJGB4: [100, 83, 83, 100, 91], //10 Vientiane
        vBWtCmNNnCG: [98, 96, 98, 100, 98], //11 Bolikhamxai
        c4HrGRJoarj: [100, 82, 83, 100, 91], //12 Khammouan
        TOgZ99Jv0bN: [96, 68, 71, 99, 83], //14 Salavan
        dOhqCNenSjS: [96, 90, 94, 100, 95], //15 Xekong
        sv6c7CpPcrc: [99, 82, 82, 100, 91], //16 Champasak
        hRQsZhmvqgS: [98, 74, 76, 100, 87], //17 Attapu
        K27JzTKmBKh: [97, 95, 98, 100, 97], //18 Xaisomboun
        // pFCZqWnXtoU: [17, 10, 62, 98, 47], //13 Savannakhet
      };

      let mockNutri = {
        W6sNfkJcXGC: [
          [
            "vTHzAXlQiF6",
            "W6sNfkJcXGC",
            "294.3",
            "13247.0",
            "4500.8",
            "100.0",
            "100",
            "1",
          ],
          [
            "uEYUCTNMfdk",
            "W6sNfkJcXGC",
            "135.5",
            "17946.0",
            "13247.0",
            "100.0",
            "100",
            "1",
          ],
          [
            "QOYBJGW55uA",
            "W6sNfkJcXGC",
            "16.2",
            "2144.0",
            "13247.0",
            "100.0",
            "100",
            "1",
          ],
          [
            "w8XQmI94Spv.REPORTING_RATE",
            "W6sNfkJcXGC",
            "92",
            "414.0",
            "450.0",
            "100",
            "",
            "",
          ],
          [
            "w8XQmI94Spv.REPORTING_RATE_ON_TIME",
            "W6sNfkJcXGC",
            "73.8",
            "332.0",
            "450.0",
            "100",
            "",
            "",
          ],
        ],
        YvLOmtTQD6b: [
          [
            "vTHzAXlQiF6",
            "W6sNfkJcXGC",
            "294.3",
            "13247.0",
            "4500.8",
            "100.0",
            "100",
            "1",
          ],
          [
            "uEYUCTNMfdk",
            "W6sNfkJcXGC",
            "135.5",
            "17946.0",
            "13247.0",
            "100.0",
            "100",
            "1",
          ],
          [
            "QOYBJGW55uA",
            "W6sNfkJcXGC",
            "16.2",
            "2144.0",
            "13247.0",
            "100.0",
            "100",
            "1",
          ],
          [
            "w8XQmI94Spv.REPORTING_RATE",
            "W6sNfkJcXGC",
            "92",
            "414.0",
            "450.0",
            "100",
            "",
            "",
          ],
          [
            "w8XQmI94Spv.REPORTING_RATE_ON_TIME",
            "W6sNfkJcXGC",
            "73.8",
            "332.0",
            "450.0",
            "100",
            "",
            "",
          ],
        ],
        XKGgynPS1WZ: [
          [
            "vTHzAXlQiF6",
            "XKGgynPS1WZ",
            "362.9",
            "4345.0",
            "1197.3",
            "100.0",
            "100",
            "1",
          ],
          [
            "uEYUCTNMfdk",
            "XKGgynPS1WZ",
            "51.6",
            "2243.0",
            "4345.0",
            "100.0",
            "100",
            "1",
          ],
          [
            "QOYBJGW55uA",
            "XKGgynPS1WZ",
            "2.0",
            "89.0",
            "4345.0",
            "100.0",
            "100",
            "1",
          ],
          [
            "w8XQmI94Spv.REPORTING_RATE",
            "XKGgynPS1WZ",
            "97.7",
            "422.0",
            "432.0",
            "100",
            "",
            "",
          ],
          [
            "w8XQmI94Spv.REPORTING_RATE_ON_TIME",
            "XKGgynPS1WZ",
            "73.6",
            "318.0",
            "432.0",
            "100",
            "",
            "",
          ],
        ],
        rO2RVJWHpCe: [
          [
            "vTHzAXlQiF6",
            "XKGgynPS1WZ",
            "362.9",
            "4345.0",
            "1197.3",
            "100.0",
            "100",
            "1",
          ],
          [
            "uEYUCTNMfdk",
            "XKGgynPS1WZ",
            "51.6",
            "2243.0",
            "4345.0",
            "100.0",
            "100",
            "1",
          ],
          [
            "QOYBJGW55uA",
            "XKGgynPS1WZ",
            "2.0",
            "89.0",
            "4345.0",
            "100.0",
            "100",
            "1",
          ],
          [
            "w8XQmI94Spv.REPORTING_RATE",
            "XKGgynPS1WZ",
            "97.7",
            "422.0",
            "432.0",
            "100",
            "",
            "",
          ],
          [
            "w8XQmI94Spv.REPORTING_RATE_ON_TIME",
            "XKGgynPS1WZ",
            "73.6",
            "318.0",
            "432.0",
            "100",
            "",
            "",
          ],
        ],
        FRmrFTE63D0: [
          [
            "vTHzAXlQiF6",
            "FRmrFTE63D0",
            "371.7",
            "4145.0",
            "1115.3",
            "100.0",
            "100",
            "1",
          ],
          [
            "uEYUCTNMfdk",
            "FRmrFTE63D0",
            "46.9",
            "1944.0",
            "4145.0",
            "100.0",
            "100",
            "1",
          ],
          [
            "QOYBJGW55uA",
            "FRmrFTE63D0",
            "2.8",
            "116.0",
            "4145.0",
            "100.0",
            "100",
            "1",
          ],
          [
            "w8XQmI94Spv.REPORTING_RATE",
            "FRmrFTE63D0",
            "95",
            "402.0",
            "423.0",
            "100",
            "",
            "",
          ],
          [
            "w8XQmI94Spv.REPORTING_RATE_ON_TIME",
            "FRmrFTE63D0",
            "87.7",
            "371.0",
            "423.0",
            "100",
            "",
            "",
          ],
        ],
        MBZYTqkEgwf: [
          [
            "vTHzAXlQiF6",
            "MBZYTqkEgwf",
            "388.4",
            "9152.0",
            "2356.5",
            "100.0",
            "100",
            "1",
          ],
          [
            "uEYUCTNMfdk",
            "MBZYTqkEgwf",
            "77.8",
            "7120.0",
            "9152.0",
            "100.0",
            "100",
            "1",
          ],
          [
            "QOYBJGW55uA",
            "MBZYTqkEgwf",
            "2.9",
            "267.0",
            "9152.0",
            "100.0",
            "100",
            "1",
          ],
          [
            "w8XQmI94Spv.REPORTING_RATE",
            "MBZYTqkEgwf",
            "99.8",
            "871.0",
            "873.0",
            "100",
            "",
            "",
          ],
          [
            "w8XQmI94Spv.REPORTING_RATE_ON_TIME",
            "MBZYTqkEgwf",
            "76.1",
            "664.0",
            "873.0",
            "100",
            "",
            "",
          ],
        ],
        hdeC7uX9Cko: [
          [
            "vTHzAXlQiF6",
            "hdeC7uX9Cko",
            "353.8",
            "6650.0",
            "1879.5",
            "100.0",
            "100",
            "1",
          ],
          [
            "uEYUCTNMfdk",
            "hdeC7uX9Cko",
            "60.0",
            "3993.0",
            "6650.0",
            "100.0",
            "100",
            "1",
          ],
          [
            "QOYBJGW55uA",
            "hdeC7uX9Cko",
            "2.8",
            "188.0",
            "6650.0",
            "100.0",
            "100",
            "1",
          ],
          [
            "w8XQmI94Spv.REPORTING_RATE",
            "hdeC7uX9Cko",
            "100",
            "779.0",
            "774.0",
            "100",
            "",
            "",
          ],
          [
            "w8XQmI94Spv.REPORTING_RATE_ON_TIME",
            "hdeC7uX9Cko",
            "92.1",
            "713.0",
            "774.0",
            "100",
            "",
            "",
          ],
        ],
        RdNV4tTRNEo: [
          [
            "vTHzAXlQiF6",
            "RdNV4tTRNEo",
            "277.3",
            "5787.0",
            "2087.0",
            "100.0",
            "100",
            "1",
          ],
          [
            "uEYUCTNMfdk",
            "RdNV4tTRNEo",
            "80.5",
            "4659.0",
            "5787.0",
            "100.0",
            "100",
            "1",
          ],
          [
            "QOYBJGW55uA",
            "RdNV4tTRNEo",
            "14.1",
            "815.0",
            "5787.0",
            "100.0",
            "100",
            "1",
          ],
          [
            "w8XQmI94Spv.REPORTING_RATE",
            "RdNV4tTRNEo",
            "97.8",
            "792.0",
            "810.0",
            "100",
            "",
            "",
          ],
          [
            "w8XQmI94Spv.REPORTING_RATE_ON_TIME",
            "RdNV4tTRNEo",
            "61.7",
            "500.0",
            "810.0",
            "100",
            "",
            "",
          ],
        ],
        VWGSudnonm5: [
          [
            "vTHzAXlQiF6",
            "VWGSudnonm5",
            "447.0",
            "6911.0",
            "1546.3",
            "100.0",
            "100",
            "1",
          ],
          [
            "uEYUCTNMfdk",
            "VWGSudnonm5",
            "68.8",
            "4755.0",
            "6911.0",
            "100.0",
            "100",
            "1",
          ],
          [
            "QOYBJGW55uA",
            "VWGSudnonm5",
            "10.0",
            "689.0",
            "6911.0",
            "100.0",
            "100",
            "1",
          ],
          [
            "w8XQmI94Spv.REPORTING_RATE",
            "VWGSudnonm5",
            "99",
            "579.0",
            "585.0",
            "100",
            "",
            "",
          ],
          [
            "w8XQmI94Spv.REPORTING_RATE_ON_TIME",
            "VWGSudnonm5",
            "90.4",
            "529.0",
            "585.0",
            "100",
            "",
            "",
          ],
        ],
        quFXhkOJGB4: [
          [
            "vTHzAXlQiF6",
            "quFXhkOJGB4",
            "330.0",
            "7149.0",
            "2166.5",
            "100.0",
            "100",
            "1",
          ],
          [
            "uEYUCTNMfdk",
            "quFXhkOJGB4",
            "90.7",
            "6482.0",
            "7149.0",
            "100.0",
            "100",
            "1",
          ],
          [
            "QOYBJGW55uA",
            "quFXhkOJGB4",
            "11.2",
            "801.0",
            "7149.0",
            "100.0",
            "100",
            "1",
          ],
          [
            "w8XQmI94Spv.REPORTING_RATE",
            "quFXhkOJGB4",
            "99.8",
            "548.0",
            "549.0",
            "100",
            "",
            "",
          ],
          [
            "w8XQmI94Spv.REPORTING_RATE_ON_TIME",
            "quFXhkOJGB4",
            "50.5",
            "277.0",
            "549.0",
            "100",
            "",
            "",
          ],
        ],
        vBWtCmNNnCG: [
          [
            "vTHzAXlQiF6",
            "vBWtCmNNnCG",
            "340.4",
            "6080.0",
            "1786.0",
            "100.0",
            "100",
            "1",
          ],
          [
            "uEYUCTNMfdk",
            "vBWtCmNNnCG",
            "56.1",
            "3412.0",
            "6080.0",
            "100.0",
            "100",
            "1",
          ],
          [
            "QOYBJGW55uA",
            "vBWtCmNNnCG",
            "7.7",
            "467.0",
            "6080.0",
            "100.0",
            "100",
            "1",
          ],
          [
            "w8XQmI94Spv.REPORTING_RATE",
            "vBWtCmNNnCG",
            "97.3",
            "438.0",
            "450.0",
            "100",
            "",
            "",
          ],
          [
            "w8XQmI94Spv.REPORTING_RATE_ON_TIME",
            "vBWtCmNNnCG",
            "97.3",
            "438.0",
            "450.0",
            "100",
            "",
            "",
          ],
        ],
        c4HrGRJoarj: [
          [
            "vTHzAXlQiF6",
            "c4HrGRJoarj",
            "268.1",
            "6272.0",
            "2339.8",
            "100.0",
            "100",
            "1",
          ],
          [
            "uEYUCTNMfdk",
            "c4HrGRJoarj",
            "85.3",
            "5353.0",
            "6272.0",
            "100.0",
            "100",
            "1",
          ],
          [
            "QOYBJGW55uA",
            "c4HrGRJoarj",
            "24.3",
            "1525.0",
            "6272.0",
            "100.0",
            "100",
            "1",
          ],
          [
            "w8XQmI94Spv.REPORTING_RATE",
            "c4HrGRJoarj",
            "99.5",
            "922.0",
            "927.0",
            "100",
            "",
            "",
          ],
          [
            "w8XQmI94Spv.REPORTING_RATE_ON_TIME",
            "c4HrGRJoarj",
            "87.1",
            "807.0",
            "927.0",
            "100",
            "",
            "",
          ],
        ],
        // pFCZqWnXtoU: [
        //   [
        //     "vTHzAXlQiF6",
        //     "TOgZ99Jv0bN",
        //     "354.5",
        //     "9514.0",
        //     "2683.8",
        //     "100.0",
        //     "100",
        //     "1",
        //   ],
        //   [
        //     "uEYUCTNMfdk",
        //     "TOgZ99Jv0bN",
        //     "59.4",
        //     "5654.0",
        //     "9514.0",
        //     "100.0",
        //     "100",
        //     "1",
        //   ],
        //   [
        //     "QOYBJGW55uA",
        //     "TOgZ99Jv0bN",
        //     "6.6",
        //     "632.0",
        //     "9514.0",
        //     "100.0",
        //     "100",
        //     "1",
        //   ],
        //   [
        //     "w8XQmI94Spv.REPORTING_RATE",
        //     "TOgZ99Jv0bN",
        //     "91.4",
        //     "683.0",
        //     "747.0",
        //     "100",
        //     "",
        //     "",
        //   ],
        //   [
        //     "w8XQmI94Spv.REPORTING_RATE_ON_TIME",
        //     "TOgZ99Jv0bN",
        //     "68.3",
        //     "510.0",
        //     "747.0",
        //     "100",
        //     "",
        //     "",
        //   ],
        // ],
        TOgZ99Jv0bN: [
          [
            "vTHzAXlQiF6",
            "TOgZ99Jv0bN",
            "354.5",
            "9514.0",
            "2683.8",
            "100.0",
            "100",
            "1",
          ],
          [
            "uEYUCTNMfdk",
            "TOgZ99Jv0bN",
            "59.4",
            "5654.0",
            "9514.0",
            "100.0",
            "100",
            "1",
          ],
          [
            "QOYBJGW55uA",
            "TOgZ99Jv0bN",
            "6.6",
            "632.0",
            "9514.0",
            "100.0",
            "100",
            "1",
          ],
          [
            "w8XQmI94Spv.REPORTING_RATE",
            "TOgZ99Jv0bN",
            "91.4",
            "683.0",
            "747.0",
            "100",
            "",
            "",
          ],
          [
            "w8XQmI94Spv.REPORTING_RATE_ON_TIME",
            "TOgZ99Jv0bN",
            "68.3",
            "510.0",
            "747.0",
            "100",
            "",
            "",
          ],
        ],
        dOhqCNenSjS: [
          [
            "vTHzAXlQiF6",
            "sv6c7CpPcrc",
            "329.6",
            "11695.0",
            "3548.8",
            "100.0",
            "100",
            "1",
          ],
          [
            "uEYUCTNMfdk",
            "sv6c7CpPcrc",
            "62.0",
            "7250.0",
            "11695.0",
            "100.0",
            "100",
            "1",
          ],
          [
            "QOYBJGW55uA",
            "sv6c7CpPcrc",
            "3.8",
            "442.0",
            "11695.0",
            "100.0",
            "100",
            "1",
          ],
          [
            "w8XQmI94Spv.REPORTING_RATE",
            "sv6c7CpPcrc",
            "96.3",
            "815.0",
            "846.0",
            "100",
            "",
            "",
          ],
          [
            "w8XQmI94Spv.REPORTING_RATE_ON_TIME",
            "sv6c7CpPcrc",
            "52.7",
            "446.0",
            "846.0",
            "100",
            "",
            "",
          ],
        ],
        sv6c7CpPcrc: [
          [
            "vTHzAXlQiF6",
            "sv6c7CpPcrc",
            "329.6",
            "11695.0",
            "3548.8",
            "100.0",
            "100",
            "1",
          ],
          [
            "uEYUCTNMfdk",
            "sv6c7CpPcrc",
            "62.0",
            "7250.0",
            "11695.0",
            "100.0",
            "100",
            "1",
          ],
          [
            "QOYBJGW55uA",
            "sv6c7CpPcrc",
            "3.8",
            "442.0",
            "11695.0",
            "100.0",
            "100",
            "1",
          ],
          [
            "w8XQmI94Spv.REPORTING_RATE",
            "sv6c7CpPcrc",
            "96.3",
            "815.0",
            "846.0",
            "100",
            "",
            "",
          ],
          [
            "w8XQmI94Spv.REPORTING_RATE_ON_TIME",
            "sv6c7CpPcrc",
            "52.7",
            "446.0",
            "846.0",
            "100",
            "",
            "",
          ],
        ],
        hRQsZhmvqgS: [
          [
            "vTHzAXlQiF6",
            "hRQsZhmvqgS",
            "372.2",
            "3985.0",
            "1070.8",
            "100.0",
            "100",
            "1",
          ],
          [
            "uEYUCTNMfdk",
            "hRQsZhmvqgS",
            "52.7",
            "2100.0",
            "3985.0",
            "100.0",
            "100",
            "1",
          ],
          [
            "QOYBJGW55uA",
            "hRQsZhmvqgS",
            "1.0",
            "40.0",
            "3985.0",
            "100.0",
            "100",
            "1",
          ],
          [
            "w8XQmI94Spv.REPORTING_RATE",
            "hRQsZhmvqgS",
            "96.9",
            "349.0",
            "360.0",
            "100",
            "",
            "",
          ],
          [
            "w8XQmI94Spv.REPORTING_RATE_ON_TIME",
            "hRQsZhmvqgS",
            "57.8",
            "208.0",
            "360.0",
            "100",
            "",
            "",
          ],
        ],
        K27JzTKmBKh: [
          [
            "vTHzAXlQiF6",
            "K27JzTKmBKh",
            "352.5",
            "2652.0",
            "752.3",
            "100.0",
            "100",
            "1",
          ],
          [
            "uEYUCTNMfdk",
            "K27JzTKmBKh",
            "56.2",
            "1491.0",
            "2652.0",
            "100.0",
            "100",
            "1",
          ],
          [
            "QOYBJGW55uA",
            "K27JzTKmBKh",
            "0.68",
            "18.0",
            "2652.0",
            "100.0",
            "100",
            "1",
          ],
          [
            "w8XQmI94Spv.REPORTING_RATE",
            "K27JzTKmBKh",
            "92.3",
            "191.0",
            "207.0",
            "100",
            "",
            "",
          ],
          [
            "w8XQmI94Spv.REPORTING_RATE_ON_TIME",
            "K27JzTKmBKh",
            "79.7",
            "165.0",
            "207.0",
            "100",
            "",
            "",
          ],
        ],
      };

      // TODO: Real data
      //mockNutri = completenessDataGroupByProvince;

      // transform data
      Object.keys(mockNutri).forEach((ou) => {
        return metadatas.forEach((dx, idx) => {
          const curValueByMetadata = mockNutri[ou].find(
            (item) => item[0] === dx
          );
          curValueByMetadata[2] = mockData?.[ou]?.[idx] || "";
          return curValueByMetadata;
        });
      });

      setDataNutri(mockNutri);

      setLoading(false);
    })();
  }, []);

  if (!dataNutri) return null;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "20px",
        height: "100%",
      }}
    >
      <Table className="table-content">
        <TableHead
          sx={{
            "& th": {
              fontSize: "12px",
            },
          }}
        >
          <TableRow>
            {/* make text aligt left */}
            <TableCell style={{ textAlign: "left" }}>{t("Province")}</TableCell>
            <TableCell>
              {t("Completeness")}{" "}
              <TooltipButton
                ranges={listLegend[0]}
                options={{ Infinity: 100 }}
              />
            </TableCell>
            <TableCell>
              {t("Timeliness")}
              <TooltipButton
                ranges={listLegend[1]}
                options={{ Infinity: 100 }}
              />
            </TableCell>
            <TableCell>
              {t("Correctness")}
              <TooltipButton
                ranges={listLegend[2]}
                options={{ Infinity: 100 }}
              />
            </TableCell>
            <TableCell>
              {t("Consistency")}
              <TooltipButton
                ranges={listLegend[3]}
                options={{ Infinity: 100 }}
              />
            </TableCell>
            <TableCell>
              {t("Overall data quality")}
              <TooltipButton
                ranges={listLegend[4]}
                options={{ Infinity: 100 }}
              />
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody
          sx={{
            "& td": {
              fontSize: "12px",
            },
          }}
        >
          {listCapitalOrDistrict.map((ou) => (
            <TableRow>
              <TableCell style={{ textAlign: "left", width: "16.5%" }}>
                {i18n.language === "lo"
                  ? ou.nameLo.slice(3)
                  : ou.nameEn.slice(3)}
              </TableCell>
              {listData.map((item) => {
                const { value, style, de } = item.getValue(ou.id) || {};
                return (
                  <TableCell
                    style={{
                      background: style,
                      fontWeight: de === "QOYBJGW55uA" ? "bold" : "",
                      width: "16.5%",
                    }}
                  >
                    {item.getValue ? value : ""}%
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
          <TableRow>
            <TableCell style={{ fontWeight: "bold", textAlign: "left" }}>
              {t("Total/ Lao PDR")}
            </TableCell>
            <TableCell style={{ fontWeight: "bold" }}>97%</TableCell>
            <TableCell style={{ fontWeight: "bold" }}>82%</TableCell>
            <TableCell style={{ fontWeight: "bold" }}>84%</TableCell>
            <TableCell style={{ fontWeight: "bold" }}>99.6%</TableCell>
            <TableCell style={{ fontWeight: "bold" }}>91%</TableCell>
          </TableRow>
        </TableBody>
      </Table>
      <div style={{ display: "flex", justifyContent: "center" }}>
        <div>
          <strong style={{ fontSize: 30 }}>Supporting Documents:</strong>
          <ul>
            <li style={{ fontSize: 20 }}>
              <a href="https://nipn.lsb.gov.la/manual-guideline/">
                SOP for routine nutrition data monitoring
              </a>
            </li>
            <li style={{ fontSize: 20 }}>
              <a href="https://nipn.lsb.gov.la/report/">RMNCAH Strategy</a>
            </li>
            <li style={{ fontSize: 20 }}>
              <a href="https://nipn.lsb.gov.la/document/target-sheet-dhis2-indicators/">
                DHIS2 Nutrition Indicators & Targets
              </a>
            </li>
          </ul>
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "center" }}>
        <img src={nutritionFooter} width={700} />
      </div>
    </Box>
  );
};

export default withWidgetChildrenLoader(WidgetDataQuality);
